import { TFunction } from 'i18next';
import numbro from 'numbro';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { participantIdColumn } from '../../../components/table/columns';
import DLTable from '../../../components/table/DLTable/DLTable';
import { StringFilter } from '../../../components/table/filters';
import { tableHelpers } from '../../../helpers';
import {
  CCASSQueryDetailsResponse,
  CCASSShareholdingDetail,
} from '../../../py-generated/datalouder/ccass/types';
import { CCASSShareholdingCostMap, Language } from '../../../shared/types';
import { ReactTable } from '../../../types';
import CCASSParticipationHistory from './CCASSParticipationHistory';
import { NOT_IN_CCASS } from './CCASSSummaryTables';

interface ShareholdingItem {
  stockCode: string;
  participantId: string;
  participantName: {
    en: string;
    zh: string;
  };
  shareholding: number;
  shareholdingOverIssuedShare: number;
  shareholdingChange?: number;
  shareholdingChangeOverIssuedShare?: number;
  avgBuy?: number;
  avgSell?: number;
  avgTrade?: number;
  totalChangeAmountByParticipant?: number;
}

export const getParticipantName = (
  participants: CCASSQueryDetailsResponse['participants'],
  id: string,
  fallback: string
): { en: string; zh: string } => {
  const name = participants[id];
  return name ? { en: name.En, zh: name.Zh } : { en: fallback, zh: fallback };
};

const makeHandleClickParticipantHistoryState = (
  t: TFunction,
  stockCode: string,
  participantId: string,
  participantName: string
) => ({
  hShowModal: true,
  hProps: {
    stockCode,
    participantId,
    participantName,
  },
  hFormatters: [
    {
      thousandSeparated: true,
      mantissa: 0,
    },
    {
      thousandSeparated: true,
      mantissa: 0,
      forceSign: true,
    },
    '0.00%',
    '+0.00%',
  ],
  hAccessors: [
    'shareholding',
    s => s.shareholding - s.previous.shareholding,
    s => s.shareholding / s.issuedShares,
    s => (s.shareholding - s.previous.shareholding) / s.issuedShares,
  ],
  hLabels: [t('Shareholding'), t('Share Change'), t('% Share'), t('% Change')],
  hTitle: `${participantId} ${participantName}`,
});

export const useHandleClickParticipantHistory = () => {
  const { t } = useTranslation();

  const [history, setHistory] = useState({
    hShowModal: false,
    hStockCode: undefined,
    hFormatters: [],
    hAccessors: [],
    hLabels: [],
    hTitle: undefined,
    hProps: undefined,
  });

  const handleClickParticipantHistory = useCallback(
    (stockCode: string, participantId: string, participantName: string) => {
      setHistory(history => ({
        ...history,
        ...makeHandleClickParticipantHistoryState(
          t,
          stockCode,
          participantId,
          participantName
        ),
      }));
    },
    [t]
  );

  const handleClose = useCallback(
    () => setHistory(history => ({ ...history, hShowModal: false })),
    []
  );

  return { history, handleClickParticipantHistory, handleClose };
};

interface Props {
  stockCode: string;
  queryIssuedShare: number;
  queryTotalShare: number;
  compareIssuedShare?: number;
  compareTotalShare?: number;
  queryDetails: CCASSShareholdingDetail[];
  compareDetails?: CCASSShareholdingDetail[];
  participants: CCASSQueryDetailsResponse['participants'];
  holdCostData?: CCASSShareholdingCostMap;
  loading?: boolean;
}

const CCASSShareholdingTable: React.FC<Props> = ({
  stockCode,
  queryIssuedShare,
  queryTotalShare,
  compareIssuedShare,
  compareTotalShare,
  queryDetails,
  compareDetails,
  participants,
  holdCostData,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const { history, handleClickParticipantHistory, handleClose } =
    useHandleClickParticipantHistory();
  const allColumns: ReactTable.Column[] = [
    participantIdColumn(t, i18n, handleClickParticipantHistory),
    {
      id: 'ccass-participant-name',
      Header: t('Name of CCASS Participant'),
      width: 40,
      accessor: (item: ShareholdingItem) =>
        item.participantName[i18n.language as Language],
      Filter: StringFilter,
      filter: 'text',
      contentAlign: 'left',
    },
    {
      id: 'ccass-shareholding',
      Header: t('Shareholding'),
      width: 70,
      accessor: (item: ShareholdingItem) => item.shareholding,
      Cell: ({ cell: { value } }) => <div>{numbro(value).format('0,0')}</div>,
    },
    {
      id: 'ccass-shareholding-change',
      Header: t('Share Change'),
      width: 70,
      accessor: (item: ShareholdingItem) => item.shareholdingChange,
      Cell: ({ cell: { value } }) => <div>{numbro(value).format('+0,0')}</div>,
      sortType: tableHelpers.sortPriortizeNonZero,
    },
    {
      id: 'ccass-%-share',
      Header: t('% Share'),
      width: 70,
      accessor: (item: ShareholdingItem) => item.shareholdingOverIssuedShare,
      Cell: ({ cell: { value } }) => <div>{numbro(value).format('0.00%')}</div>,
    },
    {
      id: 'ccass-%-change',
      Header: t('% Change'),
      width: 70,
      accessor: (item: ShareholdingItem) =>
        item.shareholdingChangeOverIssuedShare,
      Cell: ({ cell: { value } }) => (
        <div>{numbro(value).format('+0.00%')}</div>
      ),
    },
    {
      id: 'ccass-avg-buy',
      Header: t('avg buy'),
      width: 70,
      accessor: (item: ShareholdingItem) => item.avgBuy,
    },
    {
      id: 'ccass-avg-sell',
      Header: t('avg sell'),
      width: 70,
      accessor: (item: ShareholdingItem) => item.avgSell,
    },
    {
      id: 'ccass-avg-trade',
      Header: t('avg trade'),
      width: 70,
      accessor: (item: ShareholdingItem) => item.avgTrade,
    },
    {
      id: 'ccass-total-change-amount',
      Header: t('total change amount'),
      width: 100,
      accessor: (item: ShareholdingItem) => item.totalChangeAmountByParticipant,
      Cell: ({ cell: { value } }) => <div>$ {numbro(value).format('0,0')}</div>,
    },
  ];

  // if no compare date, remove "change" columns
  const visibleColumns = allColumns.filter(
    column =>
      // hide change columns when compareData does not exists or doesn't have any shareholding data
      compareDetails?.length > 0 || !column.id.endsWith('change')
  );
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const detailsAndDiff = useMemo((): ShareholdingItem[] => {
    if (!queryIssuedShare) {
      return [];
    }

    const queryItems: { [key: string]: ShareholdingItem } = {};
    for (const item of queryDetails) {
      queryItems[item.participant_id + item.participant_name] = {
        stockCode,
        participantId: item.participant_id,
        participantName: getParticipantName(
          participants,
          item.participant_id,
          item.participant_name
        ),
        shareholding: item.share_holding,
        shareholdingOverIssuedShare: item.share_holding / queryIssuedShare,
      };
    }
    queryItems['NOTINCCASS'] = {
      stockCode,
      participantId: 'NOTINCCASS',
      participantName: {
        en: t(NOT_IN_CCASS),
        zh: t(NOT_IN_CCASS),
      },
      shareholding: queryIssuedShare - queryTotalShare,
      shareholdingOverIssuedShare:
        (queryIssuedShare - queryTotalShare) / queryIssuedShare,
    };
    if (compareIssuedShare && compareDetails && compareTotalShare) {
      for (const item of compareDetails) {
        const participantKey = item.participant_id + item.participant_name;
        if (queryItems[participantKey]) {
          const shareholdingChange =
            queryItems[participantKey].shareholding - item.share_holding;
          queryItems[participantKey] = {
            ...queryItems[participantKey],
            shareholdingChange,
            shareholdingChangeOverIssuedShare:
              shareholdingChange / compareIssuedShare,
          };
        } else {
          queryItems[participantKey] = {
            stockCode,
            participantId: item.participant_id,
            participantName: getParticipantName(
              participants,
              item.participant_id,
              item.participant_name
            ),
            shareholding: 0,
            shareholdingOverIssuedShare: 0,
            shareholdingChange: -item.share_holding,
            shareholdingChangeOverIssuedShare:
              -item.share_holding / compareIssuedShare,
          };
        }
      }
      // for non-ccass
      const nonCCASS = queryIssuedShare - queryTotalShare;
      const nonCCASSChange =
        nonCCASS - (compareIssuedShare - compareTotalShare);
      queryItems['NOTINCCASS'] = {
        ...queryItems['NOTINCCASS'],
        shareholdingChange: nonCCASSChange,
        shareholdingChangeOverIssuedShare: nonCCASSChange / nonCCASS,
      };
      // for new participant
      for (const participantKey in queryItems) {
        if (queryItems[participantKey].shareholdingChange === undefined) {
          queryItems[participantKey].shareholdingChange =
            queryItems[participantKey].shareholding;
          queryItems[participantKey].shareholdingChangeOverIssuedShare =
            queryItems[participantKey].shareholdingOverIssuedShare;
        }
        if (
          holdCostData &&
          (holdCostData[queryItems[participantKey].participantId] ||
            holdCostData[queryItems[participantKey].participantName.en])
        ) {
          const key =
            queryItems[participantKey].participantId ||
            queryItems[participantKey].participantName.en;
          if (holdCostData[key]) {
            queryItems[participantKey].avgBuy = holdCostData[key].avgBuy;
            queryItems[participantKey].avgSell = holdCostData[key].avgSell;
            queryItems[participantKey].avgTrade = holdCostData[key].avgTrade;
            queryItems[participantKey].totalChangeAmountByParticipant =
              holdCostData[key].totalChangeAmountByParticipant;
          }
        }
      }
    }
    return Object.values(queryItems);
  }, [
    compareDetails,
    compareIssuedShare,
    participants,
    queryDetails,
    queryIssuedShare,
    stockCode,
    holdCostData,
    compareTotalShare,
    queryTotalShare,
    t,
  ]);

  return (
    <>
      <DLTable
        tableId="shareholding"
        columns={visibleColumns}
        data={detailsAndDiff || []}
        loading={loading}
        defaultSortBy={[
          {
            id: 'ccass-%-share',
            desc: true,
          },
        ]}
      />
      {history.hShowModal && (
        <CCASSParticipationHistory
          {...history.hProps}
          accessors={history.hAccessors}
          labels={history.hLabels}
          title={history.hTitle}
          formatters={history.hFormatters}
          onHide={handleClose}
        />
      )}
    </>
  );
};

export default CCASSShareholdingTable;
